import type {
  CollectionReference,
  PartialWithFieldValue,
  Query,
} from 'firebase/firestore'
import {
  and,
  collection,
  collectionGroup,
  doc,
  query,
  serverTimestamp,
  Timestamp,
  where,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { modelItemStream, modelListStream } from '../../firestore-mobx/stream'
import { StaticModelCollection } from '../../firestore-mobx/model'
import {
  setDocWithError,
  getDocWithError,
  getDocsWithError,
} from '../../firestore-mobx/fetch'
import type { FirestoreRoomStateUPR } from './schema'
import { empty, schema, writeSchema } from './schema'
import { RoomStateUPR } from '../../models/RoomStateUPR'
import { getColGroupRef as getPeerReviewColGroupRef } from '../RoomStateUPRPeerReview'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreRoomStateUPR> = {
  toFirestore: (data: PartialWithFieldValue<FirestoreRoomStateUPR>) => {
    writeSchema.partial().parse(data)
    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

export const getColGroupRef = (
  firestore: Firestore
): Query<FirestoreRoomStateUPR> => {
  return collectionGroup(firestore, 'user_peer_review').withConverter(converter)
}

export const getColRef = (
  firestore: Firestore,
  roomId: string
): CollectionReference<FirestoreRoomStateUPR> => {
  return collection(
    firestore,
    'room_state',
    roomId,
    'user_peer_review'
  ).withConverter(converter)
}

export const buildEmptyRoomStateUPRCollection = (
  repository: FirebaseRepository
) => {
  return new StaticModelCollection({ repository, model: RoomStateUPR, empty })
}

export const getUserPeerReviewsForRoom = (
  repository: FirebaseRepository,
  { roomId }: { roomId: string }
) => {
  const colRef = query(getColRef(repository.firestore, roomId))
  return modelListStream(repository, colRef, RoomStateUPR)
}

export const getUserPeerReviewForRoomUser = (
  repository: FirebaseRepository,
  { roomId, userId }: { roomId: string; userId: string }
) => {
  const colRef = getColRef(repository.firestore, roomId)
  const docRef = doc(colRef, userId)
  return modelItemStream(repository, docRef, RoomStateUPR)
}

export const getUserPeerReviewsForAssignment = (
  repository: FirebaseRepository,
  { sectionId, assignmentId }: { sectionId: string; assignmentId: string }
) => {
  const q = query(
    getColGroupRef(repository.firestore),
    and(
      where('sectionId', '==', sectionId),
      where('assignmentId', '==', assignmentId)
    )
  )
  return modelListStream(repository, q, RoomStateUPR)
}

export const ensureUserPeerReviewRecord = async (
  repository: FirebaseRepository,
  params: {
    roomId: string
    sectionId: string
    assignmentId: string
    userId: string
  }
) => {
  const { firestore } = repository
  const { roomId, sectionId, assignmentId, userId } = params
  const colRef = getColRef(firestore, roomId)
  const uprRef = doc(colRef, userId)

  const existingDoc = await getDocWithError(uprRef, 'EnsureUserPeerReviewError')

  if (existingDoc.exists()) {
    return uprRef
  }

  await setDocWithError(
    uprRef,
    {
      roomId,
      sectionId,
      assignmentId,
      userId,
      updatedAt: serverTimestamp() as unknown as Date,
    },
    {
      merge: true,
      errorName: 'EnsureUserPeerReviewError',
    }
  )
  return uprRef
}

/**
 * Returns an array of room IDs for which the user needs to complete a peer review.
 * A room is included if there is a user_peer_review record updated since the cutoff date
 * but no corresponding peer_review record for that room updated since the cutoff date.
 */
export const checkUserYetToCompletePeerReview = async (
  repository: FirebaseRepository,
  {
    userId,
    cutoffDate,
  }: {
    userId: string
    cutoffDate: Date
  }
): Promise<string[]> => {
  const cutoffTimestamp = Timestamp.fromDate(cutoffDate)

  const uprQuery = query(
    getColGroupRef(repository.firestore),
    and(
      where('userId', '==', userId),
      where('updatedAt', '>=', cutoffTimestamp)
    )
  )

  const uprSnapshot = await getDocsWithError(
    uprQuery,
    'CheckUserYetToCompletePeerReviewError'
  )

  if (uprSnapshot.empty) {
    return []
  }

  const roomsNeedingReview = new Set<string>()
  uprSnapshot.forEach((doc) => {
    const data = doc.data()
    if (data.roomId) {
      roomsNeedingReview.add(data.roomId)
    }
  })

  const peerReviewQuery = query(
    getPeerReviewColGroupRef(repository.firestore),
    and(
      where('reviewerUserId', '==', userId),
      where('updatedAt', '>=', cutoffTimestamp)
    )
  )

  const peerReviewSnapshot = await getDocsWithError(
    peerReviewQuery,
    'CheckUserYetToCompletePeerReviewError'
  )

  peerReviewSnapshot.forEach((doc) => {
    const data = doc.data()
    if (data.roomId) {
      roomsNeedingReview.delete(data.roomId)
    }
  })

  return Array.from(roomsNeedingReview)
}
